import { useCallback, useEffect, useMemo, useState } from 'react'

import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import isNil from 'lodash.isnil'
import omit from 'lodash.omit'
import {
  CreateViewCSVMutation,
  CreateViewCSVMutationVariables,
  CreateViewMutation,
  CreateViewMutationVariables,
  DeleteViewMutation,
  DeleteViewMutationVariables,
  GetColumnTemplatesForViewQuery,
  GetColumnTemplatesForViewQueryVariables,
  GetOrCreateViewsMutation,
  GetOrCreateViewsMutationVariables,
  GetViewDataInput,
  GetViewsDataCountsQuery,
  GetViewsDataCountsQueryVariables,
  GetViewsDataQuery,
  GetViewsDataQueryVariables,
  ReorderViewsMutation,
  ReorderViewsMutationVariables,
  UpdateViewMutation,
  UpdateViewMutationVariables,
  UpdateViewNameMutation,
  UpdateViewNameMutationVariables,
  ViewFragment,
  ViewType,
} from 'types/graphql'

import { TypedDocumentNode } from '@redwoodjs/web'

import {
  useViewDashboardByIdQuery,
  useViewsDataCountsForViewDashboardQuery,
  useViewsDataForViewDashboardQuery,
} from 'src/fetch/viewDashboards'
import { TableViewDateFilter } from 'src/models/TableViews/TableViewDateFilter'
import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from 'src/utils/queryClient'

const GetOrCreateViews = gql`
  mutation GetOrCreateViewsMutation($input: GetViewsInput!) {
    getExistingViewsOrCreateDefaultViews(input: $input) {
      ...ViewFragment
    }
  }
`

export const useGetOrCreateViewsMutation = (
  options?: UseMutationOptions<GetOrCreateViewsMutation, GetOrCreateViewsMutationVariables>
) => {
  return useMutation<GetOrCreateViewsMutation, GetOrCreateViewsMutationVariables>({
    mutationDocument: GetOrCreateViews,
    ...options,
  })
}

const GetColumnTemplatesForView = gql`
  query GetColumnTemplatesForViewQuery($input: GetViewsInput!) {
    getViewColumnTemplates(input: $input) {
      columnTemplates {
        ...ViewColumnTemplateFragment
      }
    }
  }
`

export enum TableViewMode {
  Edit = 'Edit',
  View = 'View',
}

export const useViewsQuery = (args: {
  mode: TableViewMode
  recordTypeId?: number
  viewDashboardId?: number
  viewType: ViewType
}) => {
  const { mode, recordTypeId, viewDashboardId, viewType } = args
  const [views, setViews] = useState<ViewFragment[]>([])

  const { mutateAsync: getOrCreateViews } = useGetOrCreateViewsMutation({
    onSuccess: (mutationResult) => {
      setViews(
        (mutationResult?.getExistingViewsOrCreateDefaultViews ?? []).sort(
          (a, b) => a.displayIndex - b.displayIndex
        )
      )
    },
  })
  const generateDefaultViews = useCallback(async () => {
    return await getOrCreateViews({
      input: { recordTypeId, viewDashboardId, viewType },
    })
  }, [getOrCreateViews, recordTypeId, viewDashboardId, viewType])
  useEffect(() => {
    if (viewType && mode === TableViewMode.Edit) {
      void generateDefaultViews()
    }
  }, [generateDefaultViews, mode, recordTypeId, viewType])

  const { viewDashboard } = useViewDashboardByIdQuery(viewDashboardId, {
    enabled: mode === TableViewMode.View && !!viewDashboardId,
  })
  useEffect(() => {
    if (viewDashboard && mode === TableViewMode.View) {
      setViews(viewDashboard.views)
    }
  }, [viewDashboard, mode])

  const columnTemplatesQueryResult = useQuery<
    GetColumnTemplatesForViewQuery,
    GetColumnTemplatesForViewQueryVariables
  >({
    queryKey: [QueryKey.Views, args],
    queryDocument: GetColumnTemplatesForView,
    variables: {
      input: {
        viewType: args.viewType,
        recordTypeId: args.recordTypeId,
        viewDashboardId: args.viewDashboardId,
      },
    },
    refetchOnWindowFocus: false,
    gcTime: 0,
  })

  const columnTemplates = useMemo(
    () => columnTemplatesQueryResult?.data?.getViewColumnTemplates.columnTemplates ?? [],
    [columnTemplatesQueryResult?.data?.getViewColumnTemplates.columnTemplates]
  )

  // Batched count query with isCountOnly optimization
  const { data: viewsDataCountsResult, isLoading: isLoadingViewsDataCounts } =
    useViewsDataCountsQuery(views, {
      enabled: !viewDashboardId && views.length > 0,
    })

  // For dashboard views, use the existing batched query
  const {
    data: viewsDataCountsForViewDashboardResult,
    isLoading: isLoadingViewsDataCountsForViewDashboard,
  } = useViewsDataCountsForViewDashboardQuery(
    viewDashboardId,
    views.map((v) => v.id)
  )

  // Convert counts to ViewCountResult format
  const batchedViewsDataCounts: ViewCountResult[] = useMemo(() => {
    const counts = viewsDataCountsResult?.getViewsData ?? []
    return counts.map((vc) => ({
      viewId: vc.viewId ?? -1,
      totalCount: vc.totalCount,
      isLoading: false,
    }))
  }, [viewsDataCountsResult?.getViewsData])

  // Convert dashboard counts to ViewCountResult format
  const dashboardViewsDataCounts: ViewCountResult[] = useMemo(() => {
    const dashboardCounts =
      viewsDataCountsForViewDashboardResult?.getViewsDataForViewDashboard ?? []
    return dashboardCounts.map((vc) => ({
      viewId: vc.viewId ?? -1,
      totalCount: vc.totalCount,
      isLoading: false,
    }))
  }, [viewsDataCountsForViewDashboardResult?.getViewsDataForViewDashboard])

  // Track loading state per view - all views in a batch share the same loading state
  const viewsDataCounts: ViewCountResult[] = useMemo(() => {
    if (viewDashboardId) {
      return isLoadingViewsDataCountsForViewDashboard
        ? views.map((v) => ({ viewId: v.id, totalCount: undefined, isLoading: true }))
        : dashboardViewsDataCounts
    }
    return isLoadingViewsDataCounts
      ? views.map((v) => ({ viewId: v.id, totalCount: undefined, isLoading: true }))
      : batchedViewsDataCounts
  }, [
    viewDashboardId,
    isLoadingViewsDataCountsForViewDashboard,
    isLoadingViewsDataCounts,
    views,
    dashboardViewsDataCounts,
    batchedViewsDataCounts,
  ])

  return {
    columnTemplates,
    generateDefaultViews,
    views,
    viewsDataCounts,
    isLoading: columnTemplatesQueryResult.isLoading,
  }
}

export const GetViewsDataCounts: TypedDocumentNode<
  GetViewsDataCountsQuery,
  GetViewsDataCountsQueryVariables
> = gql`
  query GetViewsDataCountsQuery($inputs: [GetViewDataInput!]!) {
    getViewsData(inputs: $inputs) {
      viewId
      totalCount
    }
  }
`
export const useViewsDataCountsQuery = (
  views: ViewFragment[],
  options?: UseQueryOptions<GetViewsDataCountsQuery, GetViewsDataCountsQueryVariables>
) => {
  const inputs: GetViewDataInput[] = useMemo(() => {
    return views.map((v) => ({
      viewId: v.id,
      viewInput: {
        displayIndex: v.displayIndex,
        name: v.name,
        viewType: v.viewType,
        columns: [], // column data is not needed for counts
        filters: v.filters.map((f) => ({
          bool: f.bool,
          columnTemplateGeneratedId: f.columnTemplateGeneratedId,
          date: TableViewDateFilter.isDatePopulated(f.date)
            ? omit(f.date, '__typename')
            : undefined,
          membership:
            f.membership && !!f.membership.type ? omit(f.membership, '__typename') : undefined,
          isNull: f.isNull,
          number: f.number ? omit(f.number, '__typename') : undefined,
        })),
        sorts: v.sorts.map((s) => ({
          columnTemplateGeneratedId: s.columnTemplateGeneratedId,
          mode: s.mode,
        })),
        recordTypeId: v.recordType?.id,
      },
      isCountOnly: true,
    }))
  }, [views])
  return useQuery<GetViewsDataCountsQuery, GetViewsDataCountsQueryVariables>({
    queryKey: [QueryKey.ViewsDataCounts, inputs.map(transformGetViewDataInputForQueryKey)],
    queryDocument: GetViewsDataCounts,
    variables: {
      inputs,
    },
    refetchOnWindowFocus: false,
    enableCancellation: true,
    ...options,
    enabled: inputs.length > 0 && (isNil(options?.enabled) || options.enabled),
  })
}

export type ViewCountResult = {
  viewId: number
  totalCount: number | undefined
  isLoading: boolean
}

const GetViewsData = gql`
  query GetViewsDataQuery($inputs: [GetViewDataInput!]!) {
    getViewsData(inputs: $inputs) {
      ...ViewDataFragment
    }
  }
`

const transformGetViewDataInputForQueryKey = (i: GetViewDataInput) => ({
  ...i,
  viewInput: omit(i.viewInput, 'name', 'displayIndex'), // avoid refetching on name or ordering changes
})

export const useViewsDataQuery = (args: {
  inputs: GetViewDataInput[]
  mode: TableViewMode
  viewDashboardId: number | null | undefined
}) => {
  const { inputs, mode, viewDashboardId } = args

  const { data, ...queryResult } = useQuery<GetViewsDataQuery, GetViewsDataQueryVariables>({
    queryKey: [QueryKey.ViewsData, inputs.map(transformGetViewDataInputForQueryKey)],
    queryDocument: GetViewsData,
    variables: {
      inputs,
    },
    enabled: mode === TableViewMode.Edit && inputs.every((i) => !!i.viewInput?.columns?.length),
    placeholderData: keepPreviousData,
    enableCancellation: true,
  })

  const { data: dashboardData, ...dashboardQueryResult } = useViewsDataForViewDashboardQuery(
    viewDashboardId ?? -1,
    inputs.map((i) => ({
      ...omit(i, 'viewInput'),
      sorts: i.viewInput?.sorts,
      filters: i.viewInput?.filters,
    })),
    {
      enabled: mode === TableViewMode.View && !!viewDashboardId && inputs.every((i) => !!i.viewId),
    }
  )

  const viewData = useMemo(
    () =>
      mode === TableViewMode.Edit
        ? data?.getViewsData?.[0]
        : dashboardData?.getViewsDataForViewDashboard?.[0],
    [data, dashboardData, mode]
  )

  return { ...(mode === TableViewMode.Edit ? queryResult : dashboardQueryResult), viewData }
}

const CreateViewCSV = gql`
  mutation CreateViewCSVMutation($fileName: String!, $input: GetViewDataInput!) {
    createViewCsv(fileName: $fileName, input: $input) {
      csvFileId
      csvUrl
    }
  }
`

export const useCreateViewCSVMutation = () => {
  return useMutation<CreateViewCSVMutation, CreateViewCSVMutationVariables>({
    mutationDocument: CreateViewCSV,
  })
}

const CreateView = gql`
  mutation CreateViewMutation($input: CreateViewInput!) {
    createView(input: $input) {
      ...ViewFragment
    }
  }
`

export const useCreateViewMutation = (
  options?: UseMutationOptions<CreateViewMutation, CreateViewMutationVariables>
) => {
  return useMutation<CreateViewMutation, CreateViewMutationVariables>({
    mutationDocument: CreateView,
    ...options,
  })
}

const UpdateView = gql`
  mutation UpdateViewMutation($id: Int!, $input: CreateViewInput!) {
    updateView(id: $id, input: $input) {
      id
      updatedAt
    }
  }
`

export const useUpdateViewMutation = (
  options?: UseMutationOptions<UpdateViewMutation, UpdateViewMutationVariables>
) => {
  return useMutation<UpdateViewMutation, UpdateViewMutationVariables>({
    mutationDocument: UpdateView,
    ...options,
  })
}

const UpdateViewName = gql`
  mutation UpdateViewNameMutation($id: Int!, $name: String!) {
    updateViewName(id: $id, name: $name) {
      id
      name
    }
  }
`

export const useUpdateViewNameMutation = (
  options: UseMutationOptions<UpdateViewNameMutation, UpdateViewNameMutationVariables>
) => {
  return useMutation<UpdateViewNameMutation, UpdateViewNameMutationVariables>({
    mutationDocument: UpdateViewName,
    ...options,
  })
}

const DeleteView = gql`
  mutation DeleteViewMutation($id: Int!) {
    deleteView(id: $id)
  }
`

export const useDeleteViewMutation = (
  options?: UseMutationOptions<DeleteViewMutation, DeleteViewMutationVariables>
) => {
  return useMutation<DeleteViewMutation, DeleteViewMutationVariables>({
    mutationDocument: DeleteView,
    ...options,
  })
}

const ReorderViews = gql`
  mutation ReorderViewsMutation($input: ReorderViewsInput!) {
    reorderViews(input: $input)
  }
`

export const useReorderViewsMutation = () => {
  return useMutation<ReorderViewsMutation, ReorderViewsMutationVariables>({
    mutationDocument: ReorderViews,
  })
}

export const useRefetchViewsDataQuery = () => {
  const queryClient = useQueryClient()
  return useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: [QueryKey.ViewsDataCounts] })
    await queryClient.refetchQueries({ queryKey: [QueryKey.ViewsData] })
    await queryClient.refetchQueries({ queryKey: [QueryKey.Views] })
  }, [queryClient])
}
